/**
 * Race sounds, all synthesized in WebAudio (no files): a motor hum that
 * follows speed, gate ticks, a lap ding (double for a new best), a pole
 * thud, a soft void blip. Subtle and enhancing, per the spec. The context
 * is created inside the flag-click gesture, so autoplay policy is satisfied.
 */

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let humGain: GainNode | null = null;
let humOsc1: OscillatorNode | null = null;
let humOsc2: OscillatorNode | null = null;

export function armAudio() {
  if (ctx) {
    if (ctx.state === "suspended") void ctx.resume();
    return;
  }
  try {
    ctx = new AudioContext();
  } catch {
    return; // no audio: the race still runs silent
  }
  master = ctx.createGain();
  master.gain.value = 0.22;
  master.connect(ctx.destination);

  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = 640;
  humGain = ctx.createGain();
  humGain.gain.value = 0;
  humOsc1 = ctx.createOscillator();
  humOsc1.type = "sawtooth";
  humOsc1.frequency.value = 52;
  humOsc2 = ctx.createOscillator();
  humOsc2.type = "sawtooth";
  humOsc2.frequency.value = 54.5;
  humOsc1.connect(filter);
  humOsc2.connect(filter);
  filter.connect(humGain);
  humGain.connect(master);
  humOsc1.start();
  humOsc2.start();
}

/** speed01 in 0..1; the quad pitch and volume ride it. */
export function setHum(speed01: number) {
  if (!ctx || !humGain || !humOsc1 || !humOsc2) return;
  const t = ctx.currentTime;
  humGain.gain.setTargetAtTime(0.045 + speed01 * 0.14, t, 0.08);
  humOsc1.frequency.setTargetAtTime(48 + speed01 * 95, t, 0.1);
  humOsc2.frequency.setTargetAtTime(50.5 + speed01 * 99, t, 0.1);
}

export function stopHum() {
  if (!ctx || !humGain) return;
  humGain.gain.setTargetAtTime(0, ctx.currentTime, 0.06);
}

function blip(freq0: number, freq1: number, dur: number, gain: number, type: OscillatorType) {
  if (!ctx || !master) return;
  const t = ctx.currentTime;
  const o = ctx.createOscillator();
  o.type = type;
  o.frequency.setValueAtTime(freq0, t);
  o.frequency.exponentialRampToValueAtTime(Math.max(1, freq1), t + dur);
  const g = ctx.createGain();
  g.gain.setValueAtTime(gain, t);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  o.connect(g);
  g.connect(master);
  o.start(t);
  o.stop(t + dur + 0.02);
}

/** Gate cleared. */
export function tick() {
  blip(1650, 1200, 0.055, 0.1, "square");
}

/** Valid lap. */
export function ding() {
  blip(880, 1175, 0.32, 0.13, "sine");
}

/** New fastest lap: a little two-note stinger. */
export function fanfare() {
  ding();
  if (!ctx) return;
  window.setTimeout(() => blip(1175, 1568, 0.36, 0.12, "sine"), 130);
}

/** Lap crossed with a missed flag: soft, low, no drama. */
export function voidBlip() {
  blip(240, 150, 0.22, 0.07, "sine");
}

/** Pole strike. */
export function thud() {
  if (!ctx || !master) return;
  const t = ctx.currentTime;
  const len = Math.floor(ctx.sampleRate * 0.22);
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / len);
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const f = ctx.createBiquadFilter();
  f.type = "lowpass";
  f.frequency.value = 330;
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.32, t);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.22);
  src.connect(f);
  f.connect(g);
  g.connect(master);
  src.start(t);
}
